/**
 * API endpoints para Nutritional Certificates (Recetas)
 * Base URL: /api/recipes/
 */

import { GET, POST, PUT, PATCH, DELETE } from "../config";

/**
 * Listar certificados nutricionales
 * @param {number} page - Número de página
 * @param {object} filters - Filtros (recipe, branch, status, search)
 * @returns {Promise} Lista de certificados
 */
export const list = async (page = 1, filters = {}) => {
  let url = `recipes/nutritional-certificates/?page=${page}&page_size=50`;

  if (filters.recipe) {
    url += `&recipe=${filters.recipe}`;
  }

  if (filters.branch) {
    url += `&branch=${filters.branch}`;
  }

  if (filters.status) {
    url += `&status=${filters.status}`;
  }

  if (filters.search) {
    url += `&recipe__name__icontains=${encodeURIComponent(filters.search)}`;
  }

  const response = await GET(url);
  return response.data;
};

/**
 * Obtener un certificado por ID
 * @param {string|number} id - ID del certificado
 * @returns {Promise} Datos del certificado
 */
export const get = async (id) => {
  const response = await GET(`recipes/nutritional-certificates/${id}/`);
  return response.data;
};

/**
 * Crear un certificado nutricional
 * @param {object} data - Datos del certificado
 * @returns {Promise} Certificado creado
 */
export const create = async (data) => {
  const response = await POST("recipes/nutritional-certificates/", data);
  return response.data;
};

/**
 * Generar certificado automáticamente desde receta
 * @param {number} recipeId - ID de la receta
 * @param {object} data - Datos adicionales (servings, portion_size, etc.)
 * @returns {Promise} Certificado generado
 */
export const generateFromRecipe = async (recipeId, data = {}) => {
  const response = await POST(
    `recipes/recipes/${recipeId}/generate_nutritional_certificate/`,
    data
  );
  return response.data;
};

/**
 * Actualizar un certificado
 * @param {string|number} id - ID del certificado
 * @param {object} data - Datos a actualizar
 * @returns {Promise} Certificado actualizado
 */
export const update = async (id, data) => {
  const response = await PUT(`recipes/nutritional-certificates/${id}/`, data);
  return response.data;
};

/**
 * Actualizar parcialmente un certificado
 * @param {string|number} id - ID del certificado
 * @param {object} data - Datos a actualizar
 * @returns {Promise} Certificado actualizado
 */
export const partialUpdate = async (id, data) => {
  const response = await PATCH(`recipes/nutritional-certificates/${id}/`, data);
  return response.data;
};

/**
 * Eliminar un certificado
 * @param {string|number} id - ID del certificado
 * @returns {Promise}
 */
export const remove = async (id) => {
  const response = await DELETE(`recipes/nutritional-certificates/${id}/`);
  return response.data;
};

/**
 * Verificar un certificado por código
 * @param {string} code - Código de verificación
 * @returns {Promise} Datos del certificado verificado
 */
export const verify = async (code) => {
  const response = await GET(
    `recipes/nutritional-certificates/verify/?code=${code}`
  );
  return response.data;
};

/**
 * Descargar certificado en PDF
 * @param {string|number} id - ID del certificado
 * @returns {Promise} Blob del PDF
 */
export const downloadPDF = async (id) => {
  const { Axios } = require("../config");
  const token = localStorage.getItem("token");

  try {
    const response = await Axios.get(
      `recipes/nutritional-certificates/${id}/download_pdf/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
        responseType: "blob",
      }
    );

    return response.data;
  } catch (error) {
    if (error.response?.data instanceof Blob) {
      const text = await error.response.data.text();
      try {
        const errorJson = JSON.parse(text);
        throw new Error(errorJson.error || "Error descargando certificado");
      } catch {
        throw new Error("Error descargando certificado");
      }
    }
    throw new Error(error.message || "Error descargando certificado");
  }
};

/**
 * Generar QR para certificado
 * @param {string|number} id - ID del certificado
 * @returns {Promise} Datos del QR generado
 */
export const generateQR = async (id) => {
  const response = await POST(
    `recipes/nutritional-certificates/${id}/generate_qr/`
  );
  return response.data;
};

/**
 * Publicar certificado (cambiar estado a published)
 * @param {string|number} id - ID del certificado
 * @returns {Promise} Certificado publicado
 */
export const publish = async (id) => {
  const response = await POST(
    `recipes/nutritional-certificates/${id}/publish/`
  );
  return response.data;
};

/**
 * Revocar certificado
 * @param {string|number} id - ID del certificado
 * @param {string} reason - Razón de revocación
 * @returns {Promise} Certificado revocado
 */
export const revoke = async (id, reason) => {
  const response = await POST(
    `recipes/nutritional-certificates/${id}/revoke/`,
    { reason }
  );
  return response.data;
};

// ==================== EXPORT ====================

const nutritionalCertificatesApi = {
  list,
  get,
  create,
  generateFromRecipe,
  update,
  partialUpdate,
  remove,
  verify,
  downloadPDF,
  generateQR,
  publish,
  revoke,
};

export default nutritionalCertificatesApi;
